import styled from '@emotion/styled/macro'
import { Box } from '@mui/material'
import theme from 'theme'

export const AssetInventoryContainer = styled(Box)`
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background: ${theme.colors.primaryDarker};
  display: flex;
  flex-direction: column;

  html[data-theme='light'] & {
    background: ${theme.colors.pageBackground};
  }
`

/* The gutters sit on the rows, not here, so the rule under the tabs still spans the full width. */
export const HeaderCard = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.large}px;
  flex-shrink: 0;
  background: ${theme.colors.surfaceContainerBackground};
  border-bottom: 1px solid ${theme.colors.borderMedium};
  padding-top: ${theme.spacing.small}px;
`

export const PageHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.medium}px;
  height: 48px;
  padding: 0 ${theme.spacing.medium}px;

  .page-title {
    color: ${theme.colors.foreground};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.xxLarge};
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 48px;
  }
`

/* The rule under the tabs belongs to the header card, so the tab group carries no border. */
export const TabBar = styled(Box)`
  padding: 0 ${theme.spacing.medium}px;

  .MuiTabs-root {
    min-height: 34px;
  }

  /* A tab is only as wide as its label; the 32px gap does the spacing. */
  .MuiTabs-flexContainer {
    gap: ${theme.spacing.large}px;
  }

  /* The underline is the tab's own border, as on the CPI details tabs, so hover can tint it. */
  .MuiTab-root {
    min-height: 34px;
    min-width: 0;
    padding: 0 0 ${theme.spacing.sm}px;
    border-bottom: 2px solid transparent;
    text-transform: none;
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.devicesSecondaryText};
    line-height: 22px;

    &:hover {
      color: ${theme.colors.menuItemText};
      border-bottom-color: ${theme.colors.legacy4836462};
    }

    &.Mui-selected {
      color: ${theme.colors.menuItemText};
      font-weight: ${theme.typography.fontWeight.extraThick};
      border-bottom-color: ${theme.colors.cta};
    }
  }
`

export const TabsPanel = styled(Box)`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.medium}px;
  padding: ${theme.spacing.medium}px ${theme.spacing.medium}px ${theme.spacing.large}px;

  ::-webkit-scrollbar {
    width: 12px;
  }

  ::-webkit-scrollbar-thumb {
    background: ${theme.colors.dropdownBorder};
    background-clip: padding-box;
    border: solid 4px transparent;
    border-radius: 8px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }
`

/* Three equal columns, as the design sizes them: the stacked number cards, the trend, the breakdown. */
export const StatisticsGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${theme.spacing.medium}px;
  align-items: stretch;

  @media (max-width: 1400px) {
    grid-template-columns: minmax(220px, 1fr) minmax(280px, 2fr);

    .breakdown-cell {
      grid-column: 1 / -1;
    }
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;

    .breakdown-cell {
      grid-column: auto;
    }
  }

  .number-cards-cell {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.medium}px;
  }
`

export const StatisticsToggle = styled('button')`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: ${theme.spacing.xSmall}px;
  color: ${theme.colors.devicesSecondaryText};
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.medium};
  font-weight: ${theme.typography.fontWeight.bold};
  line-height: 20px;

  &:hover {
    color: ${theme.colors.menuItemText};
  }

  &:focus-visible {
    outline: 1px solid ${theme.colors.cta};
    outline-offset: 2px;
  }

  /* ChevronRight paints with a fill attribute, so CSS recolours the path rather than a stroke. */
  .toggle-chevron {
    display: inline-flex;
    transform: rotate(90deg);
    transition: transform 0.2s ease;

    svg {
      width: 16px;
      height: 16px;
    }

    path {
      fill: currentColor;
    }
  }

  &[aria-expanded='false'] .toggle-chevron {
    transform: rotate(0deg);
  }
`
